import { notificationTextKey } from '@base/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type LocalizedText } from '../api.ts';
import { Button, ErrorBox, Loading, PageHeader, Panel } from '../components/ui.tsx';
import { localized } from '../i18n/index.ts';

interface NotificationItem {
  id: string;
  kind: string;
  params: Record<string, string | number | null | LocalizedText> | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

/** "Minhas pendências": os avisos da pessoa, do mais novo para o mais antigo. */
export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [onlyUnread, setOnlyUnread] = useState(true);
  const list = useQuery({
    queryKey: ['notifications', 'list', onlyUnread],
    queryFn: () =>
      api.get<{ unreadCount: number; items: NotificationItem[] }>(
        `/notifications?unread=${onlyUnread}&limit=100`,
      ),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['notifications'] });
  const read = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: refresh,
  });
  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });

  const open = (n: NotificationItem) => {
    if (!n.readAt) read.mutate(n.id);
    if (n.link) void navigate({ to: n.link });
  };
  const text = (n: NotificationItem) => {
    // Textos que o servidor manda nos dois idiomas (ex.: o nome do perfil) saem no da tela.
    const params = Object.fromEntries(
      Object.entries(n.params ?? {}).map(([k, v]) => [
        k,
        v && typeof v === 'object' && 'pt-BR' in v ? localized(v as LocalizedText) : v,
      ]),
    );
    return t(notificationTextKey(n.kind), { ...params, defaultValue: n.kind });
  };

  return (
    <>
      <PageHeader
        title={t('notifications.title')}
        description={t('notifications.description')}
        actions={
          (list.data?.unreadCount ?? 0) > 0 ? (
            <Button
              variant="secondary"
              disabled={readAll.isPending}
              onClick={() => readAll.mutate()}
            >
              {t('notifications.readAll')}
            </Button>
          ) : null
        }
      />
      <div className="flex gap-2">
        {[true, false].map((v) => (
          <Button
            key={String(v)}
            aria-pressed={onlyUnread === v}
            variant={onlyUnread === v ? 'primary' : 'secondary'}
            onClick={() => setOnlyUnread(v)}
          >
            {t(v ? 'notifications.unread' : 'notifications.all')}
          </Button>
        ))}
      </div>
      <ErrorBox error={read.error ?? readAll.error} />
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <Panel>
          {list.data.items.length === 0 ? (
            <p className="text-sm text-muted">
              {t(onlyUnread ? 'notifications.emptyUnread' : 'notifications.empty')}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-rule">
              {list.data.items.map((n) => (
                <li key={n.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => open(n)}
                    className="flex min-w-0 flex-1 flex-col gap-0.5 py-3 text-left hover:bg-paper"
                  >
                    <span className={n.readAt ? 'text-sm' : 'text-sm font-semibold'}>
                      {!n.readAt && (
                        <span className="sr-only">{t('notifications.unreadMark')} </span>
                      )}
                      {text(n)}
                    </span>
                    <span className="text-xs text-muted">{when.format(new Date(n.createdAt))}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}
    </>
  );
}
